// @react
import React, { ReactElement, useState } from 'react'
// @typings
import { CommunicationFormValues } from 'modules/common/forms/types/communication'
// @libraries
import { UseFormMethods, Control, useWatch, Controller } from 'react-hook-form'
import { makeStyles } from '@material-ui/core/styles'
import RadioGroup from '@material-ui/core/RadioGroup'
// @components
import Modal from '@bit/atd.web.modal'
import InputRadio from '@bit/atd.web.forms.input-radio'
import Button from '@bit/atd.web.button'
import colors from '@bit/atd.web.colors/dist/colors'
import strings from 'lib/constants/strings'

type PropsType = {
  formMethods: UseFormMethods<CommunicationFormValues>
  control: Control<CommunicationFormValues>
  isEditing: boolean
  watchFields: string[]
}

const useStyles = makeStyles(() => ({
  optionItem: {
    padding: '12px 24px 24px',
    borderBottom: `1px solid ${colors.gray300}`,
    '&:last-child': {
      borderBottom: 'none',
    },
    '& > div': {
      width: '100%',
    },
  },
}))

const FewerEmailOptionsFields = ({
  formMethods,
  control,
  isEditing,
  watchFields,
}: PropsType): ReactElement => {
  /****************************************
   *  STATES
   ****************************************/

  const [showModal, setShowModal] = useState<boolean>(false)
  const [modalType, setModalType] = useState<'bi-monthly' | 'unsubscribe' | ''>(
    '',
  )

  /****************************************
   *  HOOKS
   ****************************************/

  const classes = useStyles()
  const watchSelections = useWatch<CommunicationFormValues>({
    control,
    name: watchFields,
  })

  /****************************************
   *  RENDER
   ****************************************/

  let autoRenewModalTitle = ''
  if (modalType === 'unsubscribe') {
    autoRenewModalTitle =
      strings.ONBOARDING.COMMUNICATION.MODAL_CONFIRM_UNSUBSCRIBE_PROMPT
  } else if (modalType === 'bi-monthly') {
    autoRenewModalTitle =
      strings.ONBOARDING.COMMUNICATION.MODAL_CONFIRM_DIGEST_PROMPT
  }

  return (
    <>
      <RadioGroup name="fewerEmailGroup">
        {/* Bi-Monthly option */}
        <div className={classes.optionItem}>
          <InputRadio
            disabled={!isEditing}
            label={strings.ONBOARDING.COMMUNICATION.OPTION_LESS_ONE_LABEL}
            labelPlacement="right"
            size="medium"
            checked={watchSelections.fewerEmails === 'bi-monthly'}
            onChange={() => {
              if (watchSelections?.fewerEmails === 'unsubscribe') {
                formMethods.setValue('fewerEmails', 'bi-monthly', {
                  shouldValidate: false,
                  shouldDirty: true,
                })
              } else {
                setModalType('bi-monthly')
                setShowModal(true)
              }
            }}
            helperText={
              <span>
                {strings.ONBOARDING.COMMUNICATION.OPTION_LESS_ONE_DESCRIPTION}
              </span>
            }
          />
        </div>

        {/* Unsubscribe from all option */}
        <div className={classes.optionItem}>
          <InputRadio
            disabled={!isEditing}
            label="Unsubscribe From All"
            labelPlacement="right"
            size="medium"
            checked={
              watchSelections.fewerEmails === 'unsubscribe' /* || isEmpty()*/
            }
            onChange={() => {
              if (watchSelections?.fewerEmails === 'unsubscribe') {
                formMethods.setValue('fewerEmails', 'unsubscribe', {
                  shouldValidate: false,
                  shouldDirty: true,
                })
              } else {
                setModalType('unsubscribe')
                setShowModal(true)
              }
            }}
            helperText={
              <span>
                {strings.ONBOARDING.COMMUNICATION.OPTION_LESS_TWO_DESCRIPTION}
              </span>
            }
          />
        </div>
      </RadioGroup>

      <Controller
        control={control}
        name="fewerEmails"
        render={({ onChange, onBlur, value, name }) => (
          <input
            hidden
            name={name}
            value={value}
            onBlur={onBlur}
            onChange={onChange}
            type="text"
          />
        )}
      />

      {/* Modal */}
      <Modal
        forceAction
        footerNoSeparator
        headerNoSeparator
        open={showModal}
        size="medium"
        title={autoRenewModalTitle}
        actions={
          <div>
            <Button
              variant="tertiary"
              size="medium"
              onClick={() => {
                setShowModal(false)
              }}
            >
              {strings.ONBOARDING.COMMUNICATION.MODAL_KEEP_SELECTION}
            </Button>
            <Button
              variant="primary"
              size="medium"
              onClick={() => {
                // Uncheck dynamic values
                const skippedKeys = [
                  'fewerEmails',
                  'isPostalMailOptedOut',
                  'receivePromotionalCommunication',
                ]
                for (const key in watchSelections) {
                  if (
                    watchSelections.hasOwnProperty(key) &&
                    // @ts-ignore
                    watchSelections[key].length > 0 &&
                    !skippedKeys.includes(key)
                  ) {
                    formMethods.setValue(key, [])
                  }
                }
                formMethods.setValue('receivePromotionalCommunication', false)

                switch (modalType) {
                  case 'bi-monthly':
                    formMethods.setValue('fewerEmails', 'bi-monthly', {
                      shouldValidate: false,
                      shouldDirty: true,
                    })

                    setShowModal(false)
                    break
                  case 'unsubscribe':
                    formMethods.setValue('fewerEmails', 'unsubscribe', {
                      shouldValidate: false,
                      shouldDirty: true,
                    })

                    setShowModal(false)
                    break
                }
              }}
            >
              {strings.ONBOARDING.COMMUNICATION.MODAL_CONFIRM}
            </Button>
          </div>
        }
      >
        <p>{strings.ONBOARDING.COMMUNICATION.MODAL_CONFIRM_CLEAR_SELECTION}</p>
        <p>
          {
            strings.ONBOARDING.COMMUNICATION
              .MODAL_CONFIRM_CLEAR_SELECTION_DETAIL
          }
        </p>
      </Modal>
    </>
  )
}

export default FewerEmailOptionsFields
